<template>
  <PortalLayout section="Prepare" hidePortalSelector hideHeader>
    <div class="pb-4">
      <BackButton to="../../prepare"> Back to Training Programs List </BackButton>
    </div>
    <div v-if="isPropertyLoading || isTrainingProgramLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Training Program</div>
          <div>
            {{ trainingProgram?.name }}
            <span v-if="!trainingProgram?.isActive">(inactive)</span>
          </div>
        </div>
        <EditButton @click="editProgramClick">Edit</EditButton>
      </h1>
      <!-- <BaseInput label="Training Program Name" v-model="trainingProgram.name"></BaseInput>
      <BaseCheckbox label="Currently Active" v-model="trainingProgram.isActive"></BaseCheckbox>
      <br><br> -->
      <h3 class="font-medium">Modules:</h3>
      <div v-if="trainingModules && trainingModules.length > 0">
        <AddButton class="mt-2" @click="addModuleClick">Add Module</AddButton>
        <div
          v-for="module in trainingModules"
          :key="module.trainingModuleId"
          class="p-4 my-2 border-t"
          :class="module.isActive ? 'bg-gray-100' : 'bg-gray-400'"
        >
          <div class="flex">
            <div
              class="py-1 px-2 hover:bg-blue-200 flex-grow rounded cursor-pointer"
              @click="trainingModuleClick(module)"
            >
              <div>{{ module.name }} <span v-if="!module.isActive">(inactive)</span></div>
            </div>
            <EditButton @click="editModuleClick(module)" iconOnly="true" />
          </div>
        </div>
      </div>
      <div class="m-2" v-else>
        <span>-- No Training Modules found for this Training Program --</span>
      </div>
      <AddButton @click="addModuleClick">Add Module</AddButton>
      <EditPopup
        entity-name="Training Module"
        :is-visible="isModulePopupVisible"
        :is-edit-mode="isModuleEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isModulePopupVisible = false"
        @save="saveModuleClick"
        @delete="deleteModuleClick"
      >
        <BaseInput label="Name" v-model="moduleName" :error="errorsModule.moduleName" />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="moduleIsActive" />
      </EditPopup>
      <EditPopup
        entity-name="Training Program"
        :is-visible="isProgramPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isProgramPopupVisible = false"
        @save="saveProgramClick"
      >
        <BaseInput
          label="Name"
          v-model="programName"
          :error="errorsProgram.programName"
        />
        <BaseInput
          label="V4 Password"
          v-model="v4Password"
          :error="errorsProgram.v4Password"
        />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="programIsActive" />
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useTrainingStore } from "../../store/trainingStore";
import { TrainingProgramDTO, TrainingModuleDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const route = useRoute();
const router = useRouter();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isTrainingProgramLoading = ref(true);
var property = computed(() => bldgStore.property);

let trainingProgram = computed(() => trainingStore.selectedTrainingProgram);
let trainingModules = computed(() => trainingProgram.value.trainingModules);

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isTrainingProgramLoading.value = true;
  if (property.value?.propertyId) {
    if (
      route.params.trainingProgramId &&
      typeof route.params.trainingProgramId === "string"
    ) {
      let trainingProgramId = Number.parseInt(route.params.trainingProgramId);
      await trainingStore.getTrainingProgram(trainingProgramId);
    }
  }
  isTrainingProgramLoading.value = false;
}

let trainingModuleClick = function (program: TrainingModuleDTO) {
  router.push(`../trainingmodule/${program.trainingModuleId}`);
};

// Popup stuff
let isProgramPopupVisible = ref(false);
let saveError = ref("");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Training Program Edit
const { handleSubmit: handleSubmitProgram, errors: errorsProgram } = useForm({
  validationSchema: yup.object({
    programName: yup.string().required().max(100).label("Name"),
    v4Password: yup.string().max(100).nullable().label("V4 Password"),
  }),
  initialValues: {
    programName: "",
    v4Password: "",
  },
});

const { value: programName } = useField<string>("programName");
const { value: v4Password } = useField<string>("v4Password");
let programIsActive = ref(true);

let saveProgramClick = handleSubmitProgram(onValidSubmitProgram, onInvalidSubmit);

async function onValidSubmitProgram() {
  let newProgram = {
    trainingProgramId: trainingProgram.value.trainingProgramId,
    portalId: property.value.portalId,
    name: programName.value,
    isActive: programIsActive.value,
    v4Password: v4Password.value,
  } as TrainingProgramDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTrainingProgram(newProgram);
    if (!saveError.value) {
      trainingProgram.value.name = programName.value;
      trainingProgram.value.isActive = programIsActive.value;
      trainingProgram.value.v4Password = v4Password.value;
      isProgramPopupVisible.value = false;
    }
  }
}

let editProgramClick = function () {
  programName.value = trainingProgram.value.name;
  programIsActive.value = trainingProgram.value.isActive;
  v4Password.value = trainingProgram.value.v4Password;

  saveError.value = "";

  isProgramPopupVisible.value = true;
};

// Training Module Edit
let isModulePopupVisible = ref(false);
let isModuleEditing = ref(false);

const { handleSubmit: handleSubmitModule, errors: errorsModule } = useForm({
  validationSchema: yup.object({
    moduleName: yup.string().required().max(100).label("Name"),
  }),
  initialValues: {
    moduleName: "",
  },
});

let moduleId = ref(0);
const { value: moduleName } = useField<string>("moduleName");
let moduleIsActive = ref(true);

let saveModuleClick = handleSubmitModule(onValidSubmitModule, onInvalidSubmit);

async function onValidSubmitModule() {
  let newModule = {
    trainingModuleId: moduleId.value,
    trainingProgramId: trainingProgram.value.trainingProgramId,
    portalId: trainingProgram.value.portalId,
    name: moduleName.value,
    isActive: moduleIsActive.value,
  } as TrainingModuleDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTrainingModule(newModule);
    if (!saveError.value) {
      await trainingStore.getTrainingProgram(trainingProgram.value.trainingProgramId);
      isModulePopupVisible.value = false;
    }
  }
}

let addModuleClick = function () {
  moduleId.value = 0;
  moduleName.value = "";
  saveError.value = "";

  isModuleEditing.value = false;
  isModulePopupVisible.value = true;
};

let editModuleClick = function (selectedModule: TrainingModuleDTO) {
  moduleId.value = selectedModule.trainingModuleId ?? 0;
  moduleName.value = selectedModule.name ?? "";
  moduleIsActive.value = selectedModule.isActive;

  saveError.value = "";

  isModuleEditing.value = true;
  isModulePopupVisible.value = true;
};

async function deleteModuleClick() {
  if (moduleId.value) {
    console.log(`Deleting Training Module ${moduleId.value}`);
    saveError.value = await trainingStore.deleteTrainingModule(moduleId.value);
    if (!saveError.value) {
      await trainingStore.getTrainingProgram(trainingProgram.value.trainingProgramId);
      isModulePopupVisible.value = false;
    }
  }
}
</script>
